import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import {
  usePersistEstimationMutation,
  usePurchaseSolarEstimationMutation,
  useSubmitForApprovalMutation,
  useUpdateEstimationMutation,
} from "../../../redux/api/solar-estimation/solarEstimationAPI";
import { useLazyProfileQuery } from "../../../redux/api/user/userAPI";
import { selectAuth } from "../../../redux/features/auth/auth-slice";
import {
  selectCountry,
  selectProfile,
} from "../../../redux/features/user/user-slice";
import { IDataToSubmitForApproval } from "../../../screens/build-system/components/approved-system/approved-system";
import { EEstimationType } from "../../oversight-core/enums/estimation-type";
import { OvstErrorCode } from "../../oversight-core/enums/ovst-error-codes";
import { ESolarSolutionType } from "../../oversight-core/enums/sola-solution-type";
import { ESolarSimLocalStorage } from "../../oversight-core/enums/solar-sim-local-storage";
import { ETimePeriod } from "../../oversight-core/enums/time-period";
import { UserRole } from "../../oversight-core/enums/user-roles";
import { AppRoute } from "../../oversight-core/interfaces/app-routes";
import { IHttpError } from "../../oversight-core/interfaces/http-errror";
import SpinnerModal from "../../oversight-core/ui-elements/spinner/spinner";
import isEmailVerified from "../../oversight-core/utils/isEmailVerified";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../oversight-core/utils/toast";

const PublicRoute = () => {
  const navigate = useNavigate();
  const auth = useSelector(selectAuth);
  const profile = useSelector(selectProfile);
  const country = useSelector(selectCountry);

  const [getProfile, { isFetching, isLoading }] = useLazyProfileQuery();
  const [triggerPersistEstimation, { isLoading: isLoadingPersistEstimation }] =
    usePersistEstimationMutation();
  const [triggerUpdateEstimation, { isLoading: isLoadingUpdateEstimation }] =
    useUpdateEstimationMutation();
  const [submitForApproval, { isLoading: isSubmittingForApproval }] =
    useSubmitForApprovalMutation();
  const [
    triggerPurchaseSolarEstimation,
    { isLoading: isLoadingPurchaseSolarEstimation },
  ] = usePurchaseSolarEstimationMutation();

  useEffect(() => {
    if (auth.token && !profile) {
      getProfile({ role: auth.userRole })
        .unwrap()
        .catch((error: IHttpError) => {
          if (isEmailVerified(error)) {
            navigate(AppRoute.VERIFY_EMAIL);
          }
        });
      return;
    }

    const isLogin = localStorage.getItem("isLogin");
    const isPurchase = localStorage.getItem("isPurchase");

    if (auth.token && profile && isLogin === "true") {
      if (auth.userRole === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN) {
        navigate(AppRoute.ESTIMATIONS);
        localStorage.removeItem("isLogin");
        localStorage.removeItem("isPurchase");
        return;
      }

      const unparsedDataToSubmitForApproval = localStorage.getItem(
        "dataToSubmitForApproval",
      );

      if (unparsedDataToSubmitForApproval) {
        const parsedDataToSubmitForApproval: IDataToSubmitForApproval =
          JSON.parse(unparsedDataToSubmitForApproval);

        switch (parsedDataToSubmitForApproval.solarEstimationType) {
          case EEstimationType.SIMPLIFIED:
            triggerPersistEstimation({
              estimationId: auth.token
                ? parsedDataToSubmitForApproval.estimatorDetails.estimationId
                : "",
              estimationToken: auth.token
                ? ""
                : parsedDataToSubmitForApproval.estimatorDetails
                    .estimationToken,
              title: parsedDataToSubmitForApproval.title,
              usages: parsedDataToSubmitForApproval.devices
                .filter(
                  (device) =>
                    Number(device.amount) > 0 &&
                    Number(device.wattage) > 0 &&
                    Number(device.usage) > 0,
                )
                .map((device) => ({
                  name: device.deviceName,
                  type: device.deviceType,
                  amount: Number(device.amount),
                  wattage: Number(device.wattage),
                  usageInHours: Number(device.usage),
                  timePeriod: ETimePeriod.DAILY,
                })),
              isDraft: true,
              countryCode: parsedDataToSubmitForApproval.country,
            })
              .unwrap()
              .then((persistRes) => {
                triggerUpdateEstimation({
                  estimationId: auth.token
                    ? parsedDataToSubmitForApproval.estimatorDetails
                        .estimationId
                    : "",
                  estimatorToken: auth.token
                    ? ""
                    : parsedDataToSubmitForApproval.estimatorDetails
                        .estimationToken,
                  title: parsedDataToSubmitForApproval.title,
                  estimationType: parsedDataToSubmitForApproval.estimationType,
                  applicationType:
                    parsedDataToSubmitForApproval.estimationType ===
                    ESolarSolutionType.BOTH
                      ? parsedDataToSubmitForApproval.applicationType
                      : null,
                  utilityOptionType:
                    parsedDataToSubmitForApproval.estimationType ===
                    ESolarSolutionType.BOTH
                      ? parsedDataToSubmitForApproval.utilityOptionType
                      : null,
                  solarEstimationType: EEstimationType.SIMPLIFIED,
                  systemType: parsedDataToSubmitForApproval.systemType,
                  inverterPhaseType:
                    parsedDataToSubmitForApproval.inverterPhaseType,
                  monthlyRequiredEnergy:
                    parsedDataToSubmitForApproval.monthlyRequiredEnergy,
                  maxRequiredPower:
                    parsedDataToSubmitForApproval.maxRequiredPower,
                  numberOfUsers:
                    parsedDataToSubmitForApproval.estimationType ===
                    ESolarSolutionType.BOTH
                      ? parsedDataToSubmitForApproval.numberOfUsers
                      : null,
                  demandPerUser:
                    parsedDataToSubmitForApproval.estimationType ===
                    ESolarSolutionType.BOTH
                      ? parsedDataToSubmitForApproval.demandPerUser
                      : null,
                  nightLoad: parsedDataToSubmitForApproval.nightLoad,
                  sunlightHours: parsedDataToSubmitForApproval.sunlightHours,
                  systemVoltage: parsedDataToSubmitForApproval.systemVoltage,
                  autonomyDays: parsedDataToSubmitForApproval.autonomyDays,
                  estimationSpaceClusterId: null,
                  estimationBasicScheduleId: persistRes.scheduleId,
                  isDraft: false,
                  countryCode: country,
                  installationCostRequested:
                    parsedDataToSubmitForApproval.includeOtherFees,
                })
                  .unwrap()
                  .then(() => {
                    if (isPurchase === "true") {
                      triggerPurchaseSolarEstimation({
                        estimationId:
                          parsedDataToSubmitForApproval.estimatorDetails
                            .estimationId,
                      })
                        .unwrap()
                        .then(() => {
                          localStorage.removeItem(
                            ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                          );
                          localStorage.removeItem("simplifiedSave");

                          localStorage.removeItem(
                            ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                          );
                          localStorage.removeItem("directSave");

                          localStorage.removeItem(
                            ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                          );
                          showSuccessMessage("System purchased successfully");
                        })
                        .catch((error: IHttpError) => {
                          if (
                            error.status === 412 &&
                            error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
                          ) {
                            showErrorMessage(
                              "Solar estimation is yet to be reviewed.",
                            );
                            return;
                          }
                          showErrorMessage("Failed to purchase system");
                        })
                        .finally(() => {
                          localStorage.removeItem("dataToSubmitForApproval");
                          localStorage.removeItem("isLogin");
                          localStorage.removeItem("isPurchase");

                          const redirect = localStorage.getItem("redirect");
                          if (redirect) {
                            localStorage.removeItem("redirect");
                            navigate(redirect);
                            return;
                          }

                          navigate(AppRoute.ESTIMATIONS);
                        });
                      return;
                    }

                    submitForApproval({
                      estimationId: auth.token
                        ? parsedDataToSubmitForApproval.estimatorDetails
                            .estimationId
                        : "",
                      estimatorToken: auth.token
                        ? ""
                        : parsedDataToSubmitForApproval.estimatorDetails
                            .estimationToken,
                      includeInstallationCost:
                        parsedDataToSubmitForApproval.includeOtherFees,
                      address: parsedDataToSubmitForApproval.address,
                      note: parsedDataToSubmitForApproval.note,
                      longitude: parsedDataToSubmitForApproval.longitude,
                      latitude: parsedDataToSubmitForApproval.latitude,
                    })
                      .unwrap()
                      .then(() => {
                        localStorage.removeItem(
                          ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                        );
                        localStorage.removeItem("simplifiedSave");

                        localStorage.removeItem(
                          ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                        );
                        localStorage.removeItem("directSave");

                        localStorage.removeItem(
                          ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                        );
                        localStorage.removeItem("waterHeaterSave");

                        showSuccessMessage(
                          "Submitted for approval successfully",
                        );
                      })
                      .catch(() => {
                        showErrorMessage("Failed to submit for approval");
                      })
                      .finally(() => {
                        localStorage.removeItem("dataToSubmitForApproval");
                        localStorage.removeItem("isLogin");

                        const redirect = localStorage.getItem("redirect");
                        if (redirect) {
                          localStorage.removeItem("redirect");
                          navigate(redirect);
                          return;
                        }

                        navigate(AppRoute.ESTIMATIONS);
                      });
                  })
                  .catch(() => {
                    showErrorMessage("Failed to submit for approval");
                    localStorage.removeItem("isLogin");

                    const redirect = localStorage.getItem("redirect");
                    if (redirect) {
                      localStorage.removeItem("redirect");
                      navigate(redirect);
                      return;
                    }

                    navigate(AppRoute.ESTIMATIONS);
                  });
              })
              .catch(() => {
                showErrorMessage("Failed to submit for approval");
                localStorage.removeItem("isLogin");
                localStorage.removeItem("isPurchase");

                const redirect = localStorage.getItem("redirect");
                if (redirect) {
                  localStorage.removeItem("redirect");
                  navigate(redirect);
                  return;
                }

                navigate(AppRoute.ESTIMATIONS);
              });
            break;
          case EEstimationType.DIRECT:
            triggerUpdateEstimation({
              estimationId: auth.token
                ? parsedDataToSubmitForApproval.estimatorDetails.estimationId
                : "",
              estimatorToken: auth.token
                ? ""
                : parsedDataToSubmitForApproval.estimatorDetails
                    .estimationToken,
              title: parsedDataToSubmitForApproval.title,
              estimationType: parsedDataToSubmitForApproval.estimationType,
              applicationType:
                parsedDataToSubmitForApproval.estimationType ===
                ESolarSolutionType.BOTH
                  ? parsedDataToSubmitForApproval.applicationType
                  : null,
              utilityOptionType:
                parsedDataToSubmitForApproval.estimationType ===
                ESolarSolutionType.BOTH
                  ? parsedDataToSubmitForApproval.utilityOptionType
                  : null,
              solarEstimationType: EEstimationType.DIRECT,
              systemType: parsedDataToSubmitForApproval.systemType,
              inverterPhaseType:
                parsedDataToSubmitForApproval.inverterPhaseType,
              monthlyRequiredEnergy:
                parsedDataToSubmitForApproval.monthlyRequiredEnergy,
              maxRequiredPower: parsedDataToSubmitForApproval.maxRequiredPower,
              numberOfUsers:
                parsedDataToSubmitForApproval.estimationType ===
                ESolarSolutionType.BOTH
                  ? parsedDataToSubmitForApproval.numberOfUsers
                  : null,
              demandPerUser:
                parsedDataToSubmitForApproval.estimationType ===
                ESolarSolutionType.BOTH
                  ? parsedDataToSubmitForApproval.demandPerUser
                  : null,
              nightLoad: parsedDataToSubmitForApproval.nightLoad,
              sunlightHours: parsedDataToSubmitForApproval.sunlightHours,
              systemVoltage: parsedDataToSubmitForApproval.systemVoltage,
              autonomyDays: parsedDataToSubmitForApproval.autonomyDays,
              estimationSpaceClusterId:
                parsedDataToSubmitForApproval.estimationSpaceClusterId,
              estimationBasicScheduleId: null,
              isDraft: false,
              countryCode: country,
              installationCostRequested:
                parsedDataToSubmitForApproval.includeOtherFees,
            })
              .unwrap()
              .then(() => {
                if (isPurchase === "true") {
                  triggerPurchaseSolarEstimation({
                    estimationId:
                      parsedDataToSubmitForApproval.estimatorDetails
                        .estimationId,
                  })
                    .unwrap()
                    .then(() => {
                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                      );
                      localStorage.removeItem("simplifiedSave");

                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                      );
                      localStorage.removeItem("directSave");

                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                      );
                      localStorage.removeItem("waterHeaterSave");
                      showSuccessMessage("System purchased successfully");
                    })
                    .catch((error: IHttpError) => {
                      if (
                        error.status === 412 &&
                        error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
                      ) {
                        showErrorMessage(
                          "Solar estimation is yet to be reviewed.",
                        );
                        return;
                      }
                      showErrorMessage("Failed to purchase system");
                    })
                    .finally(() => {
                      localStorage.removeItem("dataToSubmitForApproval");
                      localStorage.removeItem("isLogin");
                      localStorage.removeItem("isPurchase");

                      const redirect = localStorage.getItem("redirect");
                      if (redirect) {
                        localStorage.removeItem("redirect");
                        navigate(redirect);
                        return;
                      }

                      navigate(AppRoute.ESTIMATIONS);
                    });
                  return;
                }

                submitForApproval({
                  estimationId: auth.token
                    ? parsedDataToSubmitForApproval.estimatorDetails
                        .estimationId
                    : "",
                  estimatorToken: auth.token
                    ? ""
                    : parsedDataToSubmitForApproval.estimatorDetails
                        .estimationToken,
                  includeInstallationCost:
                    parsedDataToSubmitForApproval.includeOtherFees,
                  address: parsedDataToSubmitForApproval.address,
                  note: parsedDataToSubmitForApproval.note,
                  longitude: parsedDataToSubmitForApproval.longitude,
                  latitude: parsedDataToSubmitForApproval.latitude,
                })
                  .unwrap()
                  .then(() => {
                    localStorage.removeItem(
                      ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                    );
                    localStorage.removeItem("simplifiedSave");

                    localStorage.removeItem(
                      ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                    );
                    localStorage.removeItem("directSave");

                    localStorage.removeItem(
                      ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                    );
                    localStorage.removeItem("waterHeaterSave");

                    showSuccessMessage("Submitted for approval successfully");
                  })
                  .catch(() => {
                    showErrorMessage("Failed to submit for approval");
                  })
                  .finally(() => {
                    localStorage.removeItem("dataToSubmitForApproval");
                    localStorage.removeItem("isLogin");
                    localStorage.removeItem("isPurchase");

                    const redirect = localStorage.getItem("redirect");
                    if (redirect) {
                      localStorage.removeItem("redirect");
                      navigate(redirect);
                      return;
                    }

                    navigate(AppRoute.ESTIMATIONS);
                  });
              })
              .catch(() => {
                showErrorMessage("Failed to submit for approval");
                localStorage.removeItem("isLogin");

                const redirect = localStorage.getItem("redirect");
                if (redirect) {
                  localStorage.removeItem("redirect");
                  navigate(redirect);
                  return;
                }

                navigate(AppRoute.ESTIMATIONS);
              });
            break;
          case EEstimationType.WATER_HEATER:
            triggerUpdateEstimation({
              estimationId: auth.token
                ? parsedDataToSubmitForApproval.estimatorDetails.estimationId
                : "",
              estimatorToken: auth.token
                ? ""
                : parsedDataToSubmitForApproval.estimatorDetails
                    .estimationToken,
              title: parsedDataToSubmitForApproval.title,
              estimationType: parsedDataToSubmitForApproval.estimationType,
              applicationType: parsedDataToSubmitForApproval.applicationType,
              utilityOptionType:
                parsedDataToSubmitForApproval.utilityOptionType,
              solarEstimationType: null,
              systemType: null,
              inverterPhaseType: null,
              monthlyRequiredEnergy: null,
              maxRequiredPower: null,
              numberOfUsers: parsedDataToSubmitForApproval.numberOfUsers,
              demandPerUser: parsedDataToSubmitForApproval.demandPerUser,
              nightLoad: null,
              sunlightHours: null,
              systemVoltage: null,
              autonomyDays: null,
              estimationSpaceClusterId: null,
              estimationBasicScheduleId: null,
              isDraft: false,
              countryCode: country,
              installationCostRequested:
                parsedDataToSubmitForApproval.includeOtherFees,
            })
              .unwrap()
              .then(() => {
                if (isPurchase === "true") {
                  triggerPurchaseSolarEstimation({
                    estimationId:
                      parsedDataToSubmitForApproval.estimatorDetails
                        .estimationId,
                  })
                    .unwrap()
                    .then(() => {
                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                      );
                      localStorage.removeItem("waterHeaterSave");

                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                      );
                      localStorage.removeItem("simplifiedSave");

                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                      );
                      localStorage.removeItem("directSave");
                      showSuccessMessage("System purchased successfully");
                    })
                    .catch((error: IHttpError) => {
                      if (
                        error.status === 412 &&
                        error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
                      ) {
                        showErrorMessage(
                          "Solar estimation is yet to be reviewed.",
                        );
                        return;
                      }
                      showErrorMessage("Failed to purchase system");
                    })
                    .finally(() => {
                      localStorage.removeItem("dataToSubmitForApproval");
                      localStorage.removeItem("isLogin");
                      localStorage.removeItem("isPurchase");

                      const redirect = localStorage.getItem("redirect");
                      if (redirect) {
                        localStorage.removeItem("redirect");
                        navigate(redirect);
                        return;
                      }

                      navigate(AppRoute.ESTIMATIONS);
                    });
                  return;
                }

                submitForApproval({
                  estimationId: auth.token
                    ? parsedDataToSubmitForApproval.estimatorDetails
                        .estimationId
                    : "",
                  estimatorToken: auth.token
                    ? ""
                    : parsedDataToSubmitForApproval.estimatorDetails
                        .estimationToken,
                  includeInstallationCost:
                    parsedDataToSubmitForApproval.includeOtherFees,
                  address: parsedDataToSubmitForApproval.address,
                  note: parsedDataToSubmitForApproval.note,
                  longitude: parsedDataToSubmitForApproval.longitude,
                  latitude: parsedDataToSubmitForApproval.latitude,
                })
                  .unwrap()
                  .then(() => {
                    localStorage.removeItem(
                      ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                    );
                    localStorage.removeItem("waterHeaterSave");

                    localStorage.removeItem(
                      ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                    );
                    localStorage.removeItem("simplifiedSave");

                    localStorage.removeItem(
                      ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                    );
                    localStorage.removeItem("directSave");

                    showSuccessMessage("Submitted for approval successfully");
                  })
                  .catch(() => {
                    showErrorMessage("Failed to submit for approval");
                  })
                  .finally(() => {
                    localStorage.removeItem("dataToSubmitForApproval");
                    localStorage.removeItem("isLogin");
                    localStorage.removeItem("isPurchase");

                    const redirect = localStorage.getItem("redirect");
                    if (redirect) {
                      localStorage.removeItem("redirect");
                      navigate(redirect);
                      return;
                    }

                    navigate(AppRoute.ESTIMATIONS);
                  });
              })
              .catch(() => {
                showErrorMessage("Failed to submit for approval");
                const redirect = localStorage.getItem("redirect");
                if (redirect) {
                  localStorage.removeItem("redirect");
                  navigate(redirect);
                  return;
                }

                navigate(AppRoute.ESTIMATIONS);
              });
            break;
          default:
            break;
        }

        return;
      }

      localStorage.removeItem("isLogin");
      const redirect = localStorage.getItem("redirect");
      if (redirect) {
        localStorage.removeItem("redirect");
        navigate(redirect);
        return;
      }
      navigate(AppRoute.ESTIMATIONS);
    }
  }, [auth.token, auth.userRole, profile]);

  return (
    <div>
      {!isFetching && !isLoading && <Outlet />}
      <SpinnerModal
        show={
          isLoading ||
          isFetching ||
          isSubmittingForApproval ||
          isLoadingPersistEstimation ||
          isLoadingUpdateEstimation ||
          isLoadingPurchaseSolarEstimation
        }
      />
    </div>
  );
};

export default PublicRoute;
